import { FontWeight, Heading, LiliLink, Text } from '@lili/lili-frontend-common';
import { Trans, useTranslation } from 'react-i18next';
import { ReactComponent as SuccessIcon } from '@Assets/icons/success.svg';
import { useGetApyDetails } from '@Hooks/api/useGetApyDetails';
import miniDashboardUrl from '@Assets/banners/mini-dashboard.svg';

import { EmbeddedSupportDetails } from '@Components/supportDetails/EmbeddedSupportDetails';
interface CheckMarkListItemProps {
  value: string | React.ReactNode;
  suffixContent?: React.ReactNode;
}

const CheckMarkListItem = ({ value, suffixContent }: CheckMarkListItemProps) => {
  return (
    <li className='flex items-start gap-[8px]'>
      <div className='w-[16px] h-[16px]'>
        <SuccessIcon className='text-outline-primary-bw-05' />
      </div>

      <span className='flex gap-[2px]'>
        <Text.H11 className='text-text-main-primary-inverse'>{value}</Text.H11>
        {suffixContent}
      </span>
    </li>
  );
};
interface CheckMarkListProps {
  items: CheckMarkListItemProps[];
}
const CheckMarkList = ({ items }: CheckMarkListProps) => {
  return (
    <ul className='flex flex-col gap-[4px]'>
      {items.map((item) => (
        <CheckMarkListItem
          key={typeof item.value === 'string' ? item.value : Math.random().toString()}
          value={item.value}
          suffixContent={item.suffixContent}
        />
      ))}
    </ul>
  );
};
export const ValuesProposalPasswordless = () => {
  const { t } = useTranslation();

  const { data: apyDetails } = useGetApyDetails();
  return (
    <div className='tab-landscape:max-w-[100%] max-w-[420px] tab-landscape:pt-[90px] pt-[24px] tab-landscape:px-[64px] sm:px-0 px-[16px] mb-[16px]'>
      <div className='flex flex-col gap-[56px] tab-landscape:min-h-[80dvh] '>
        <div className='flex flex-col gap-[8px] tab-landscape:gap-[24px]'>
          <Heading.H4
            fontWeight={FontWeight.SEMIBOLD}
            className='max-w-[469px] w-full text-text-main-primary-inverse text-[22px] leading-[1.1] whitespace-pre-line'>
            <Trans
              t={t}
              i18nKey='pages.homePasswordless.valueProposal.mainHeadline'
              components={{
                serif: (
                  <span className='font-sourceSansProSemiBold text-[24px] text-text-brand-highlight-orange' />
                ),
                orange: <span className='text-text-brand-highlight-orange' />,
              }}
            />
          </Heading.H4>
          <CheckMarkList
            items={[
              { value: t('pages.homePasswordless.valueProposal.noHiddenFees') },
              { value: t('pages.homePasswordless.valueProposal.expressACH') },
              { value: t('pages.homePasswordless.valueProposal.creditBuildingTool') },
              {
                value: (
                  <Trans
                    i18nKey='pages.homePasswordless.valueProposal.annualPercentageYield'
                    components={{
                      sup: <sup />,
                    }}
                    values={{ apyRate: apyDetails?.apyUpperBound }}
                  />
                ),
                suffixContent: (
                  <LiliLink
                    id='details-link'
                    title={t('pages.homePasswordless.valueProposal.detailsLink')}
                    href='https://lili.co/legal-documents/300-promotion-terms-and-conditions'
                    className='text-text-main-tertiary-inverse text-[12px]'
                    target='_blank'
                  />
                ),
              },
              { value: t('pages.homePasswordless.valueProposal.fdicInsurance') },
            ]}
          />
        </div>
        {/* needs to be replaced with the mobile picture - but for now its the same */}
        <div className='tab-landscape:hidden  overflow-hidden mb-[24px]'>
          <img src={miniDashboardUrl} alt='Mini dashboard' className='w-[100%] max-w-[420px]' />
        </div>
        <div className='hidden tab-landscape:block mb-[24px]'>
          <img src={miniDashboardUrl} alt='Mini dashboard' className='w-[100%]' />
        </div>
      </div>
      <div className='tablet:hidden'>
        <EmbeddedSupportDetails mb='12px' />
      </div>
      <div className='flex flex-col'>
        <ol className='list-decimal text-[10px] text-text-main-primary-inverse pl-[8px]' type='1'>
          <li>
            <Text.H14 className='text-text-main-primary-inverse'>
              {t('pages.homePasswordless.valueProposal.APYLegal', {
                apyEffectiveDate: apyDetails?.apyEffectiveDate,
                apyText: apyDetails?.apyLongDescription,
              })}
            </Text.H14>
          </li>
          <li>
            <Text.H14 className='text-text-main-primary-inverse'>
              {t('pages.homePasswordless.valueProposal.DNBScoresLegal')}
            </Text.H14>
          </li>
        </ol>
        <div className='flex flex-col gap-[4px] tab-landscape:bg-transparent mt-[8px] mb-3'>
          <Text.H14 fontWeight={FontWeight.SEMIBOLD} className='text-text-main-primary-inverse'>
            {t('pages.homePasswordless.valueProposal.USAPatriotNotice')}
          </Text.H14>
          <Text.H14 className='text-text-main-primary-inverse'>
            {t('pages.homePasswordless.valueProposal.USAPatriotNoticeContent')}
          </Text.H14>
          <Text.H14 className='text-text-main-primary-inverse' id='value-proposal-disclaimer'>
            {t('pages.homePasswordless.valueProposal.disclaimer')}
          </Text.H14>
        </div>
      </div>
    </div>
  );
};
